import { raw } from '../api/http'
import { ApiError } from '../api/problem'
import type { TokenPair, Uloga } from '../api/types'
import { pamtiPolaziste } from '../lib/polaziste'
import { procitajToken } from './jwt'

/**
 * Sesija kao vanjski store — jedna izvedba, VIŠE instanci.
 *
 * Zašto više: gost i osoblje se u istom pregledniku ne smiju gaziti (zadatak 7
 * §2). Recepcioner koji na svom mobitelu isproba aplikaciju za goste ne smije
 * time izgubiti svoju smjenu, a gost koji dobije uređaj s pulta ne smije naslijediti
 * tuđu konzolu. Odvojene su TRI stvari: ključ u pohrani, access token u memoriji
 * i pretplatnici — pa jedna prijava ne dira drugu ni u jednom smjeru.
 *
 * CLAUDE.md §13: u `localStorage` smiju samo refresh token i odabrani jezik.
 * Access token zato živi ISKLJUČIVO u memoriji — preživi li osvježavanje
 * stranice, preživio bi i tuđi XSS, a ionako traje 15 minuta i vraća se
 * refreshom. Osvježavanje stranice tako uvijek ide kroz `/auth/refresh`, što je
 * i jedino mjesto na kojem poslužitelj još jednom kaže vrijedi li sesija
 * (ugašen resort, deaktiviran račun, istekao boravak — API §1, §6).
 */

export interface SessionState {
  /** `null` dok se prva obnova iz pohrane ne dovrši — sprječava treptaj prijave. */
  ucitavanje: boolean
  accessToken: string | null
  uloga: Uloga | null
  userId: string | null
  tenantId: string | null
  /** Ime prijavljenog osoblja za zaglavlje; `null` kod gosta i prije prijave. */
  ime: string | null
  /** Dok stoji, sve osim `/auth/*` vraća 403 `password_change_required` (API §1). */
  moraPromijenitiLozinku: boolean
}

export interface SessionStore {
  getState(): SessionState
  subscribe(slusac: () => void): () => void
  getAccessToken(): string | null
  /**
   * Postoji li u ovom pregledniku zatečena sesija koju vrijedi podići — dakle
   * refresh token, prije nego što ga je itko zamijenio za access token.
   *
   * Nije zamjena za `accessToken`: ne kaže da sesija VRIJEDI (to zna samo
   * poslužitelj), nego da ima što pitati. Korijen aplikacije (`app/Korijen.tsx`)
   * time bira KUDA poslati korisnika, a dokaz ostavlja okviru na koji ga šalje.
   */
  imaZapamcenuSesiju(): boolean
  /** Preuzima par tokena nakon prijave, verifikacije ili promjene lozinke. */
  primi(par: TokenPair): void
  /**
   * `potroseni` je token s kojim je pozivatelj upravo dobio 401. Stoji li u
   * sesiji već DRUGI token, netko je u međuvremenu osvježio i rotacije više
   * nema smisla — vraća se zatečeni. Bez tog podatka svaki 401 rotira nanovo.
   */
  osvjezi(potroseni?: string | null): Promise<string | null>
  /** Pokretanje aplikacije: sesija se podiže iz pohranjenog refresh tokena. */
  obnovi(): Promise<void>
  odjavi(): Promise<void>
  /** Samo za testove: vraća instancu u polazno stanje. */
  _reset(): void
}

const POCETNO: SessionState = {
  ucitavanje: true,
  accessToken: null,
  uloga: null,
  userId: null,
  tenantId: null,
  ime: null,
  moraPromijenitiLozinku: false,
}

export function stvoriSesiju(kljucPohrane: string): SessionStore {
  let stanje: SessionState = POCETNO

  /**
   * Rezerva za slučaj da je pohrana zabranjena (privatni način rada): tada sesija
   * traje do zatvaranja kartice, umjesto da prijava odmah propadne.
   */
  let refreshUMemoriji: string | null = null

  const pretplatnici = new Set<() => void>()

  /** Jedno osvježavanje u letu: pet paralelnih 401-ica ne smije rotirati token pet puta. */
  let uLetu: Promise<string | null> | null = null

  function postavi(novo: SessionState): void {
    stanje = novo
    for (const slusac of pretplatnici) slusac()
  }

  function procitajPohranu(): string | null {
    try {
      return globalThis.localStorage?.getItem(kljucPohrane) ?? null
    } catch {
      // Privatni način rada zna zabraniti pohranu; sesija tada traje do zatvaranja kartice.
      return null
    }
  }

  function zapisiPohranu(vrijednost: string | null): void {
    try {
      if (vrijednost === null) globalThis.localStorage?.removeItem(kljucPohrane)
      else globalThis.localStorage?.setItem(kljucPohrane, vrijednost)
    } catch {
      /* Bez pohrane sesija ne preživi osvježavanje stranice — prijava i dalje radi. */
    }
  }

  /**
   * Refresh token se ČITA iz pohrane pri svakom pozivu, a ne kešira u modulu: dvije
   * kartice iste konzole dijele pohranu, pa bi keširana vrijednost jedne od njih
   * poslije rotacije bila zastarjela i sesija bi pukla bez razloga.
   */
  function refreshToken(): string | null {
    return procitajPohranu() ?? refreshUMemoriji
  }

  function primi(par: TokenPair): void {
    const claims = procitajToken(par.access_token)

    refreshUMemoriji = par.refresh_token
    zapisiPohranu(par.refresh_token)

    postavi({
      ucitavanje: false,
      accessToken: par.access_token,
      uloga: claims.uloga,
      userId: claims.sub,
      tenantId: claims.tenantId,
      ime: claims.ime,
      moraPromijenitiLozinku: claims.moraPromijenitiLozinku,
    })
  }

  function ocisti(): void {
    refreshUMemoriji = null
    zapisiPohranu(null)
    postavi({ ...POCETNO, ucitavanje: false })
  }

  async function izvediOsvjezavanje(): Promise<string | null> {
    const token = refreshToken()
    if (!token) {
      postavi({ ...POCETNO, ucitavanje: false })
      return null
    }

    try {
      const par = await raw<TokenPair>('/auth/refresh', {
        metoda: 'POST',
        tijelo: { refresh_token: token },
      })

      primi(par)
      return par.access_token
    } catch (greska) {
      // 401/403 znači da sesije više nema (istekla, ugašen resort, deaktiviran
      // račun, istekao boravak gosta). Mrežni kvar NE briše sesiju — inače bi
      // kratak prekid odjavio korisnika usred posla.
      if (greska instanceof ApiError && greska.status !== 0) {
        ocisti()
      } else {
        postavi({ ...stanje, ucitavanje: false })
      }
      return null
    }
  }

  /**
   * Jedno osvježavanje po potrošenom tokenu.
   *
   * `uLetu` sam po sebi hvata samo pozive koji se PREKLAPAJU. Zahtjevi koji
   * padnu na 401 jedan za drugim (popis, boravak, stanice, hub) ne preklapaju
   * se — svaki bi dobio svoju rotaciju, pa je dev gledao niz uspješnih
   * `/auth/refresh` bez ijednog drugog poziva između njih (zadatak 10 §1).
   * Zato drugi uvjet: nosi li sesija već drukčiji token od onoga s kojim je
   * pozivatelj pao, osvježavanje se VEĆ dogodilo i vraća se zatečeni token.
   */
  function osvjezi(potroseni?: string | null): Promise<string | null> {
    if (uLetu) return uLetu

    if (potroseni !== undefined && stanje.accessToken !== null && stanje.accessToken !== potroseni) {
      return Promise.resolve(stanje.accessToken)
    }

    uLetu = izvediOsvjezavanje().finally(() => {
      uLetu = null
    })

    return uLetu
  }

  const sesija: SessionStore = {
    getState: () => stanje,

    subscribe(slusac) {
      pretplatnici.add(slusac)
      return () => pretplatnici.delete(slusac)
    },

    getAccessToken: () => stanje.accessToken,

    imaZapamcenuSesiju: () => refreshToken() !== null,

    primi,

    osvjezi,

    /**
     * Podizanje sesije pri pokretanju. Idempotentno: sesija koja već stoji se ne
     * podiže drugi put. Okvir sučelja zna se odmontirati i vratiti (StrictMode,
     * promjena rute, povratak u aplikaciju), a svako bi podizanje inače
     * rotiralo refresh token i tuklo poslužitelja bez ijednog novog podatka.
     */
    async obnovi() {
      if (stanje.accessToken) return

      if (!refreshToken()) {
        postavi({ ...POCETNO, ucitavanje: false })
        return
      }

      await osvjezi()
    },

    /**
     * `POST /auth/logout` je anoniman — refresh token je dokaz vlasništva nad
     * sesijom (API §1). Lokalno stanje se briše u svakom slučaju: odjava koja
     * ovisi o mreži nije odjava.
     */
    async odjavi() {
      const token = refreshToken()
      ocisti()

      if (!token) return

      try {
        await raw<void>('/auth/logout', { metoda: 'POST', tijelo: { refresh_token: token } })
      } catch {
        /* Poslužitelj će token ionako pustiti da istekne. */
      }
    },

    _reset() {
      refreshUMemoriji = null
      uLetu = null
      stanje = POCETNO
      pretplatnici.clear()
    },
  }

  // Sesija je procesno stanje i mora se moći vratiti na polazište između dva
  // testa (D15-A §1a). Prijavljuje se SAMA: dotad je svaka mount datoteka morala
  // zvati `_resetSession`, a ona koja bi zaboravila nije padala sama nego bi
  // srušila sljedeću. Obje instance (osoblje i gost) time su pokrivene istim
  // retkom — a on je jedini koji o tome mora znati.
  pamtiPolaziste(sesija._reset)

  return sesija
}
